<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import ConversationAPI from 'dashboard/api/inbox/conversation';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

// Midaz Hub incident products (IncidentProduct in the Hub). Product names are not translated.
const HUB_PRODUCTS = [
  { value: 'midaz_web', label: 'Midaz Web' },
  { value: 'app_pay', label: 'App-Pay' },
  { value: 'atlas', label: 'Atlas' },
  { value: 'hka_bridge', label: 'HKA Bridge' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'fila_cero', label: 'Fila Cero' },
];
const DEFAULT_PRODUCT = 'midaz_web';
const TITLE_MIN_LENGTH = 3;
const TITLE_MAX_LENGTH = 160;
const DESCRIPTION_MAX_LENGTH = 5000;
const DESCRIPTION_MESSAGE_COUNT = 5;

const { t } = useI18n();
const dialogRef = ref(null);
const isLoading = ref(false);
const createdIncident = ref(null);

const currentChat = useMapGetter('getSelectedChat');
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);

const incident = computed(
  () =>
    createdIncident.value ||
    conversationMetadataGetter.value(props.conversationId)
      ?.additional_attributes?.hub_incident ||
    null
);

const productOptions = computed(() => [
  ...HUB_PRODUCTS,
  { value: 'other', label: t('MIDAZ_HUB_INCIDENT.PRODUCT_OTHER') },
]);

const form = reactive({ title: '', product: DEFAULT_PRODUCT, description: '' });

const isFormInvalid = computed(
  () => form.title.trim().length < TITLE_MIN_LENGTH || !form.product
);

const customerMessages = () =>
  (currentChat.value.messages || [])
    .filter(
      message =>
        message.message_type === MESSAGE_TYPE.INCOMING && message.content
    )
    .slice(-DESCRIPTION_MESSAGE_COUNT)
    .map(message => message.content.trim());

const open = () => {
  const messages = customerMessages();
  form.title = (messages[messages.length - 1] || '').slice(0, TITLE_MAX_LENGTH);
  form.product = DEFAULT_PRODUCT;
  form.description = messages.join('\n\n').slice(0, DESCRIPTION_MAX_LENGTH);
  dialogRef.value?.open();
};

const handleConfirm = async () => {
  if (isFormInvalid.value) return;

  isLoading.value = true;
  try {
    const { data } = await ConversationAPI.createHubIncident(
      props.conversationId,
      {
        title: form.title.trim(),
        product: form.product,
        description: form.description.trim(),
      }
    );
    createdIncident.value = data;
    useAlert(t('MIDAZ_HUB_INCIDENT.SUCCESS', { code: data.code }));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.response?.data?.error || t('MIDAZ_HUB_INCIDENT.ERROR'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="px-4 pb-2">
    <div v-if="incident" class="flex items-center justify-between gap-2">
      <div class="flex flex-col">
        <span class="text-sm font-medium text-n-slate-12">
          {{ incident.code }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('MIDAZ_HUB_INCIDENT.LINKED') }}
        </span>
      </div>
      <a
        :href="incident.url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-blue-11 hover:underline"
      >
        {{ t('MIDAZ_HUB_INCIDENT.OPEN_IN_HUB') }}
        <span class="i-lucide-external-link size-3.5" />
      </a>
    </div>
    <template v-else>
      <p class="mb-2 text-xs text-n-slate-11">
        {{ t('MIDAZ_HUB_INCIDENT.HINT') }}
      </p>
      <Button
        :label="t('MIDAZ_HUB_INCIDENT.CREATE')"
        icon="i-lucide-life-buoy"
        size="sm"
        variant="faded"
        color="slate"
        class="w-full"
        @click="open"
      />
    </template>
    <Dialog
      ref="dialogRef"
      :title="t('MIDAZ_HUB_INCIDENT.DIALOG_TITLE')"
      :description="t('MIDAZ_HUB_INCIDENT.DIALOG_DESCRIPTION')"
      :confirm-button-label="t('MIDAZ_HUB_INCIDENT.SUBMIT')"
      :is-loading="isLoading"
      :disable-confirm-button="isFormInvalid"
      @confirm="handleConfirm"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.title"
          :label="t('MIDAZ_HUB_INCIDENT.TITLE_LABEL')"
          :placeholder="t('MIDAZ_HUB_INCIDENT.TITLE_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('MIDAZ_HUB_INCIDENT.PRODUCT_LABEL') }}
          </label>
          <Select
            v-model="form.product"
            class="!w-full [&>select]:w-full"
            :options="productOptions"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('MIDAZ_HUB_INCIDENT.DESCRIPTION_LABEL') }}
          </label>
          <TextArea
            v-model="form.description"
            class="w-full"
            :max-length="DESCRIPTION_MAX_LENGTH"
            show-character-count
            auto-height
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
